import React, { useState } from 'react';
import GovernanceRequestModal from '../../components/GovernanceRequestModal/GovernanceRequestModal';
import '../../styles/kyro-design.css';

const GOVERNANCE_DOCS: { key: string; display: string; label: string }[] = [
  { key: 'ppm', display: '📑  Private Placement Memorandum (PPM)', label: 'Private Placement Memorandum (PPM)' },
  { key: 'contribution-agreement', display: '📜  Contribution Agreement', label: 'Contribution Agreement' },
  { key: 'sebi-compliance', display: '🔒  SEBI Compliance Filings', label: 'SEBI Compliance Filings' },
];

const govItems = [
  { l: 'Fund Structure', v: 'Category II AIF – Closed-Ended Trust', n: 'Regulated under SEBI (Alternative Investment Funds) Regulations, 2012' },
  { l: 'SEBI Status', v: 'SEBI Registered (Reg. No. IN/AIF2/26-27/2191)', n: 'Registered with SEBI as a Category II Alternative Investment Fund' },
  { l: 'Registered Office', v: 'Indore, Madhya Pradesh', n: '303, Unity One, Rajiv Gandhi Square, Indore – 452001' },
  { l: 'Trustee', v: 'Orbis Trusteeship Services Pvt. Ltd.', n: 'Independent trustee overseeing fund governance and investor protection' },
  { l: 'Valuation', v: 'Independent Valuation Partner', n: 'Bi-annual valuation per IPEV guidelines and SEBI AIF Master Circular' },
  { l: 'Compliance Officer', v: 'CS Khushi Jain', n: 'Dedicated in-house compliance ensuring continuous SEBI AIF adherence' },
];

const riskItems = [
  { t: '25% Concentration Cap', d: 'No single investment may exceed 25% of Investable Funds' },
  { t: 'Zero Leverage Policy', d: 'Fund shall not borrow except short-term operational needs' },
  { t: 'No Related-Party Transactions', d: 'Prohibited without Super-Majority Investor approval' },
  { t: 'Independent Valuation', d: 'Bi-annual by SEBI-qualified valuer per IPEV guidelines' },
];

const InvestorRelations: React.FC = () => {
  const [govSelection, setGovSelection] = useState<{ documentKey: string; documentLabel: string } | null>(null);

  return (
    <div className="page">
      <div className="hero" style={{ minHeight: '44vh', paddingBottom: '4rem' }}>
        <div className="hero-grid" />
        <div className="hero-inner">
          <div className="kicker">
            <div className="kicker-dot" />
            <div className="kicker-line" />
            Investor Relations
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            Transparency. Governance.<br />
            <span className="dim">Investor-First Reporting.</span>
          </h1>
          <p className="hero-sub">
            Structured reporting, governance oversight and compliance frameworks designed to institutional standards.
          </p>
        </div>
      </div>

      <div className="sec-off">
        <div className="eyebrow">Governance & Compliance</div>
        <div className="sec-title">SEBI-Regulated. Institutionally Governed.</div>
        <div className="gov-grid">
          {govItems.map((g) => (
            <div className="gov-cell" key={g.l}>
              <div className="gov-lbl">{g.l}</div>
              <div className="gov-val">{g.v}</div>
              <div className="gov-note">{g.n}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="sec">
        <div className="eyebrow">Reports & Communications</div>
        <div className="sec-title">Investor Reporting Framework.</div>
        <div className="grid-2" style={{ marginTop: '2rem' }}>
          <div className="card">
            <div className="card-navy-top" />
            <div style={{ padding: '1.6rem' }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '1rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '0.5rem' }}>Periodic Reports</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--gray)', lineHeight: 1.7, marginBottom: '1rem' }}>
                Structured reporting to investors on fund performance, portfolio developments and market commentary — on a scheduled basis post-deployment.
              </div>
              <div className="ir-docs">
                {['📄  Monthly Factsheets', '📊  Quarterly Reports', '📋  Annual Reports'].map((d) => (
                  <div className="ir-doc" key={d}>{d}</div>
                ))}
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-navy-top" />
            <div style={{ padding: '1.6rem' }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '1rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '0.5rem' }}>Governance Documents</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--gray)', lineHeight: 1.7, marginBottom: '1rem' }}>
                All regulatory filings and fund governance documents maintained in accordance with SEBI AIF Regulations, 2012.
              </div>
              <div className="ir-docs">
                {GOVERNANCE_DOCS.map((d) => (
                  <button
                    type="button"
                    className="ir-doc"
                    key={d.key}
                    onClick={() => setGovSelection({ documentKey: d.key, documentLabel: d.label })}
                  >
                    {d.display}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="notice">
          Documents will be made available to registered investors post first close. For access or investor grievance redressal, contact: <strong>compliance@kyrocapital.in</strong>
        </div>
      </div>

      <div className="sec-off">
        <div className="eyebrow">Risk Management</div>
        <div className="sec-title">Structured. Proactive. Transparent.</div>
        <div className="grid-2" style={{ alignItems: 'start' }}>
          <p className="sec-body">
            We have a comprehensive risk management framework. At the company level, risk monitoring ensures proactive identification and management of existing and emerging risks. We conduct risk-assessment at various stages of every investment — including asset review, regular monitoring, risk categorization and valuation.
          </p>
          <div>
            {riskItems.map((r) => (
              <div className="risk-item" key={r.t}>
                <div className="risk-icon">⊡</div>
                <div>
                  <div className="risk-title">{r.t}</div>
                  <div className="risk-desc">{r.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <GovernanceRequestModal
        open={!!govSelection}
        selection={govSelection}
        onClose={() => setGovSelection(null)}
      />
    </div>
  );
};

export default InvestorRelations;
